import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { UserActivityLogGrid } from "./UserActivityLogGrid"
import { mutateUrls } from "../../../lib/useGenericSWR";
import { useSWRConfig } from "swr";
//@ts-ignore
import { EnsureUser } from '../../auth/EnsureUser';
//@ts-ignore
import { UcListPage } from 'components/common/UcListPage';
import { useTranslation } from 'react-i18next';
import { useUserAccessStats } from './UserAccessStatsAPIEndpoints';
import { useUserEventLogs } from './UserEventLogsAPIEndpoints';
// @ts-ignore
import { PageContext, PageState, PageAction } from 'lib/PageProvider';
import { useMyTimezone } from '../../customer/orgs/MyOrgAPIEndpoints';
import { UcLoading } from '../../common/UcLoading';
// @ts-ignore
import { DateTime } from 'luxon';

interface UserActivityLogPageProps {
    userId: number;
    email: string;
    dataState?: any;
    sort?: any;
    generalSearch?: string;
    lockoutEnd: string | undefined;
}

export const UserActivityLogPage = (props: UserActivityLogPageProps) => {
    const { t } = useTranslation();
    const timezone = useMyTimezone();
    const userId = props.userId;
    const email = props.email;

    const [pageState, pageDispatch] = useContext<[PageState, React.Dispatch<PageAction>]>(PageContext);
    const initialSort = [{ field: "id", sort: "desc" }];
    const sort = props.sort ?? initialSort;

    const initialDataState = props.dataState ?? { skip: 0, take: 10, };

    const filter ={
        take: initialDataState.take,
        skip: initialDataState.skip,
        sort: sort,
        userId: userId,
        generalSearch: props.generalSearch,
    };

    const eventLogFilter = {
        email: email,
        eventTsUtc: null
    };

    const [reMutateUrls, setReMutateUrls] = useState(false);
    const swrConfig = useSWRConfig();

    const swrUserAccessStats = useUserAccessStats(filter);
    const userAccessStatsResult = swrUserAccessStats?.result?.[0] ?? {};

    const swrUserEventLogs = useUserEventLogs(eventLogFilter);

    const lastLogin = swrUserEventLogs?.result?.length > 0
        ? (() => {
            const successfulLogins = swrUserEventLogs.result
                .filter((event: { eventTypeDisplayName: string, eventStatusDisplayName: string }) =>
                    event.eventTypeDisplayName === "Login" && event.eventStatusDisplayName.includes("Success"));

            return successfulLogins.length > 0
                ? successfulLogins
                    .map((event: { eventTsUtc: string }) =>
                        DateTime.fromISO(event.eventTsUtc, { zone: "utc" })
                    )
                    .reduce((latest: DateTime, current: DateTime) =>
                        current > latest ? current : latest)
                    .setZone(timezone)
                    .toFormat("yyyy-MM-dd t")
                : "Never";
        })()
        : "Never";

    const totalLogins = swrUserEventLogs?.result
        ? swrUserEventLogs.result.filter((event: { eventTypeDisplayName: string, eventStatusDisplayName: string }) => event.eventTypeDisplayName.includes("Login") && event.eventStatusDisplayName.includes("Success")).length
        : 0;

    useLayoutEffect(() => {
        if (reMutateUrls) {
            mutateUrls(swrConfig);
            setReMutateUrls(false);
        }
    }, [reMutateUrls, swrConfig]);

    useEffect(() => {
        pageDispatch({
            header: t("User Activity Logs"),
            actionItems: [] as any[],
            variableHeader: "",
            tabs: [],
            onTabChange: () => { },
            tabValue: ''
        });
    }, [pageState.header, pageDispatch, t]);

    console.log("UserActivityLogPage - Loaded");
    return (
        <UcListPage>
            <EnsureUser permissions={["UserAccessStats:Read"]}>
                <Typography variant="h6" sx={{ mb: 1 }}>{t("User Activity Summary")}</Typography>
                <UcLoading isLoading={swrUserEventLogs?.result === undefined} hasFade={false} height={"100px" }>
                    <Paper elevation={0} sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                                <Typography data-cy-id={"user_activity_log_last_login"} color="text.secondary">{t("Last Login:")}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography>{lastLogin}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography data-cy-id={"user_activity_log_total_logins"} color="text.secondary">{t("Total Logins:")}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography>{totalLogins}</Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                    <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                                <Typography data-cy-id={"user_activity_log_invite_count"} color="text.secondary">{t("Invite Count:")}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography>{userAccessStatsResult?.inviteCount ?? 0}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography data-cy-id={"user_activity_log_locked_out"} color="text.secondary">{t("Locked Out:")}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography>{props?.lockoutEnd && new Date(props.lockoutEnd) > new Date() ? t("Yes") : t("No")}</Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </UcLoading>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{t("Activity History")}</Typography>
                {!reMutateUrls &&
                    <UserActivityLogGrid
                        hideEmailColumn={true}
                        swrUserEventLogs={swrUserEventLogs}
                    />
                }
            </EnsureUser>
        </UcListPage>
    );
};